import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'logo-only' | 'text-only' | 'combined' | 'stacked' | 'minimal' | 'product-primary';
  className?: string;
  onClick?: () => void;
}

const sizeClasses = {
  sm: 'h-6 w-auto',
  md: 'h-8 w-auto', 
  lg: 'h-12 w-auto',
  xl: 'h-16 w-auto'
};

const textSizeClasses = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-xl',
  xl: 'text-2xl'
};

export const Logo: React.FC<LogoProps> = ({ 
  size = 'md', 
  variant = 'combined',
  className = '',
  onClick 
}) => {
  const { theme } = useTheme();
  const [imageError, setImageError] = React.useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  // Use color logo for light theme, white logo for dark theme
  const logoSrc = theme === 'light' 
    ? "https://otomeytfiles.s3.ap-south-1.amazonaws.com/OtomeytLogo/OtomeytLogo_ColorVer.png"
    : "https://otomeytfiles.s3.ap-south-1.amazonaws.com/OtomeytLogo/OtomeytLogo_WhiteVer.png";

  const renderLogo = () => (
    <div className="relative">
      {!imageError ? (
        <img 
          src={logoSrc}
          alt="Otomeyt Logo"
          className={`${sizeClasses[size]} object-contain ${theme === 'light' ? '' : 'filter brightness-0 invert'}`}
          onError={handleImageError}
        />
      ) : (
        // Fallback text logo
        <div className={`${sizeClasses[size]} bg-blue-600 rounded-lg flex items-center justify-center`}>
          <span 
            className="text-white font-bold"
            style={{fontFamily: 'Monotype Corsiva, cursive', fontWeight: 'normal'}}
          >
            M
          </span>
        </div>
      )}
    </div>
  );

  const renderText = () => (
    <div className="flex flex-col items-start">
      <span 
        className={`${textSizeClasses[size]} text-text-enterprise-primary font-bold`}
        style={{fontFamily: 'Monotype Corsiva, cursive', fontWeight: 'normal'}}
      >
        Techmate
      </span>
    </div>
  );

  const renderMinimal = () => (
    <div className="flex flex-col items-center text-center">
      <span 
        className={`${textSizeClasses[size]} text-text-enterprise-primary font-bold`}
        style={{fontFamily: 'Monotype Corsiva, cursive', fontWeight: 'normal'}}
      >
        Techmate
      </span>
      <div className="mt-2">
        {renderLogo()}
      </div>
    </div>
  );

  const renderStacked = () => (
    <div className="flex flex-col items-center text-center space-y-2">
      <span 
        className={`${textSizeClasses[size]} text-text-enterprise-primary font-bold`}
        style={{fontFamily: 'Monotype Corsiva, cursive', fontWeight: 'normal'}}
      >
        Techmate
      </span>
      <div className={theme === 'light' ? 'opacity-100' : 'opacity-60'}>
        {renderLogo()}
      </div>
    </div>
  );

  const renderCombined = () => (
    <div className="flex items-center gap-3">
      {renderText()}
      <div className={theme === 'light' ? 'opacity-100' : 'opacity-60'}>
        {renderLogo()}
      </div>
    </div>
  );

  const renderProductPrimary = () => (
    <div className="flex items-center gap-4">
      <div className="flex flex-col items-start">
        <span 
          className={`${size === 'sm' ? 'text-xl' : size === 'md' ? 'text-3xl' : size === 'lg' ? 'text-4xl' : 'text-5xl'} text-text-enterprise-primary font-bold`}
          style={{fontFamily: 'Monotype Corsiva, cursive', fontWeight: 'normal'}}
        >
          Techmate
        </span>
      </div>
      <div className={theme === 'light' ? 'opacity-100' : 'opacity-40'}>
        {renderLogo()}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (variant) {
      case 'logo-only':
        return renderLogo();
      case 'text-only':
        return renderText();
      case 'stacked':
        return renderStacked();
      case 'minimal':
        return renderMinimal();
      case 'product-primary':
        return renderProductPrimary();
      case 'combined':
      default:
        return renderCombined();
    }
  };

  return (
    <div 
      className={`${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''} ${className}`}
      onClick={onClick}
    >
      {renderContent()}
    </div>
  );
};

export default Logo;
